import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Wallet, Check, Clock, ExternalLink } from "lucide-react";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function PaymentsPanel() {
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/me/custom-charges");
      setLatest(data?.latest || null);
    } catch (e) {
      setLatest(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Poll every 45s while a payment is pending so paid state appears automatically
    const i = setInterval(load, 45000);
    return () => clearInterval(i);
  }, []);

  if (loading) return null;
  if (!latest) return null;

  // Hide paid items after 24h to keep the dashboard clean
  const isPaid = latest.payment_status === "paid";
  const isProcessing = latest.payment_status === "processing";
  const isPending = !isPaid && !isProcessing;
  if (isPaid && latest.created_at) {
    const ageMs = Date.now() - new Date(latest.created_at).getTime();
    if (Number.isFinite(ageMs) && ageMs > ONE_DAY_MS) return null;
  }

  const badge = isPaid ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-full bg-green-500/15 text-green-300 border border-green-500/25" data-testid="payment-status-paid">
      <Check className="w-3 h-3" /> Paid
    </span>
  ) : isProcessing ? (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25" data-testid="payment-status-processing">
      <Clock className="w-3 h-3" /> Processing
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] rounded-full bg-[var(--if-gold)]/15 text-[var(--if-gold)] border border-[var(--if-gold)]/30" data-testid="payment-status-pending">
      <Clock className="w-3 h-3" /> Awaiting payment
    </span>
  );

  return (
    <div className="card-dark p-6" data-testid="payments-panel">
      <div className="flex items-center gap-2 mb-4">
        <Wallet className="w-4 h-4 text-[var(--if-gold)]" />
        <span className="font-mono-label text-[var(--if-gold)]">Payments</span>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-display text-lg leading-tight truncate" data-testid="payment-description">{latest.description}</div>
          <div className="text-xs text-white/55 mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
            {fmtDate(latest.created_at)}
            {latest.last4 ? ` · card •••• ${latest.last4}` : ""}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-2xl text-[var(--if-gold)]" data-testid="payment-amount">${latest.amount.toFixed(2)}</div>
          <div className="mt-1">{badge}</div>
        </div>
      </div>

      {isPending && (
        <p className="text-[12px] text-white/55 mt-4 leading-relaxed" style={{ fontFamily: "Inter, sans-serif" }}>
          Your consultant is setting up this payment. You&apos;ll get an emailed receipt once it&apos;s captured — no action needed from you.
        </p>
      )}

      {isPaid && latest.receipt_url && (
        <a
          href={latest.receipt_url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-outline !py-2 !px-3 !text-[11px] mt-4 inline-flex items-center gap-1.5"
          data-testid="payment-receipt-link"
        >
          <ExternalLink className="w-3.5 h-3.5" /> View Stripe receipt
        </a>
      )}
    </div>
  );
}
